import { Check as CheckIcon, Edit2, Lock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';
import { FieldLabel } from './FieldLabel';

// EditableField + ReadOnlyField — label + input + edit/save/cancel pattern.
// Ported verbatim from clientView/myAccount/myAccount.jsx (now removed there).
//
// EditableField props:
//   label, icon?, value, displayValue?, editing, temp,
//   onTempChange, onEdit, onSave, onCancel,
//   saving?, placeholder?, type? = 'text', mono?
//
// ReadOnlyField props:
//   label, icon?, value, hint?

export function EditableField({
  label,
  icon,
  value,
  displayValue,
  editing,
  temp,
  onTempChange,
  onEdit,
  onSave,
  onCancel,
  saving,
  placeholder,
  type = 'text',
  mono,
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1">
      <FieldLabel icon={icon}>{label}</FieldLabel>
      <div className="flex items-stretch gap-2">
        <input
          type={type}
          value={editing ? temp : (displayValue ?? value ?? '')}
          onChange={(e) => onTempChange(e.target.value)}
          disabled={!editing}
          placeholder={placeholder}
          className={cn(
            'h-10 flex-1 min-w-0 rounded-lg border border-border bg-bg px-3 text-sm text-fg',
            'disabled:bg-bg disabled:text-fg-muted disabled:cursor-not-allowed',
            'focus:outline-none focus:ring-2 focus:ring-brand-500',
            mono && 'font-mono',
          )}
        />
        <div className="flex items-center gap-1 shrink-0">
          {!editing ? (
            <button
              type="button"
              onClick={onEdit}
              className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-border bg-bg text-fg-muted hover:bg-bg-muted transition-colors"
              aria-label={t('Editar {{label}}', { label })}
              title={t('Editar {{label}}', { label })}
            >
              <Edit2 className="w-4 h-4" />
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 transition-colors"
                aria-label={t('Guardar {{label}}', { label })}
                title={t('Guardar {{label}}', { label })}
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-border bg-bg text-fg-muted hover:bg-bg-muted disabled:opacity-50 transition-colors"
                aria-label={t('Cancelar edición de {{label}}', { label })}
                title={t('Cancelar')}
              >
                <X className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

EditableField.displayName = 'EditableField';

export function ReadOnlyField({ label, icon, value, hint }) {
  return (
    <div className="space-y-1">
      <FieldLabel icon={icon}>{label}</FieldLabel>
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          value={value || ''}
          readOnly
          disabled
          className="h-10 flex-1 min-w-0 rounded-lg border border-border bg-bg px-3 text-sm text-fg-muted cursor-not-allowed"
        />
        {hint && (
          <span
            className="h-10 w-10 inline-flex items-center justify-center text-fg-muted shrink-0"
            title={hint}
          >
            <Lock className="w-4 h-4" />
          </span>
        )}
      </div>
    </div>
  );
}

ReadOnlyField.displayName = 'ReadOnlyField';

export default EditableField;
